/**
 * One turn of the chat: the message goes out, a reply comes back, or an `AssistantError` says why
 * not — and, above all, whether the week may already have changed.
 *
 * This talks to `fetch` directly instead of going through `apiRequest`. The shared client folds
 * every 429 into one message and a timeout into "check your connection", and the chat needs more
 * than that: three different 429s, and the difference between "nothing left the browser" and "it
 * may have swapped a dish and we lost the answer". Getting that second one wrong is how someone
 * ends up told their menu is unchanged when it is not.
 *
 * Nothing here retries. A turn can change the saved week, so a retry we did not ask for could
 * change it twice; "try again" only ever puts the text back in the composer.
 */
import { ASSISTANT_ENDPOINTS } from "./endpoints";
import { AssistantError, type AssistantFailureKind } from "./errors";
import type { AssistantRequest, AssistantTurn } from "./types";

/** The server's own deadline is 25 s; this leaves it room to answer before we stop waiting. */
export const ASSISTANT_TIMEOUT_MS = 30_000;

interface ErrorBody {
  error?: string | { type?: string; message?: string; retryAfter?: number };
  message?: string;
  scope?: string;
}

async function readErrorBody(response: Response): Promise<ErrorBody> {
  try {
    return (await response.json()) as ErrorBody;
  } catch {
    return {};
  }
}

function retryAfterFrom(response: Response, body: ErrorBody): number | undefined {
  const fromBody = typeof body.error === "object" ? body.error?.retryAfter : undefined;
  if (typeof fromBody === "number" && Number.isFinite(fromBody)) return fromBody;
  const header = Number(response.headers.get("Retry-After"));
  return Number.isFinite(header) && header > 0 ? header : undefined;
}

const errorCode = (body: ErrorBody): string =>
  typeof body.error === "string" ? body.error : (body.error?.type ?? "");

/**
 * Which 429 this is. The chat's per-minute limiter answers `AssistantRateLimit` with
 * `scope: 'minute'`; the day's spend cap arrives as a `RateLimitError` from the turn itself. The
 * API's general limiter also says `TooManyRequests`, and that is a minute-shaped wait too.
 */
function rateLimitKind(body: ErrorBody): AssistantFailureKind {
  if (errorCode(body) === "RateLimitError") return "rate_daily";
  if (body.scope === "day") return "rate_daily";
  return "rate_minute";
}

function kindForStatus(status: number, body: ErrorBody): AssistantFailureKind {
  if (status === 400) return "bad_request";
  if (status === 401 || status === 403) return "session";
  // The server says the assistant is off with a 404 and that exact message (see com/assistantChat.js).
  if (status === 404) return "disabled";
  if (status === 429) return rateLimitKind(body);
  // A gateway that answered for the server tells us nothing about what the server did.
  if (status === 502 || status === 504) return "unknown";
  if (status === 503) return "unavailable";
  return "server";
}

/**
 * A fetch that rejects can mean a connection that never opened or one that dropped while the turn
 * ran, and nothing in the error tells them apart (Chrome says "Failed to fetch", Safari "Load
 * failed", either way). The turn can take 25 s and the server answers only at the end, so a drop
 * mid-wait is the likely case — and calling it "never sent" would tell someone their menu is
 * unchanged when a dish may have been swapped. So: our own timeout, someone else's abort, or a
 * network failure that may have happened after the request left.
 */
function kindForNetworkFailure({ timedOut, aborted }: { timedOut: boolean; aborted: boolean }): AssistantFailureKind {
  if (timedOut) return "timeout";
  if (aborted) return "unknown";
  return "offline_after";
}

/**
 * A 200 whose body is not JSON: the turn ran, so the week may have changed, and the connection is
 * fine — «Alguna cosa no ha anat bé», not «Comprova la connexió». A drop while reading the body is
 * still a lost connection, and falls through to the network case.
 */
async function readTurn(response: Response): Promise<{ data?: AssistantTurn }> {
  try {
    return (await response.json()) as { data?: AssistantTurn };
  } catch (error) {
    if (error instanceof SyntaxError) throw new AssistantError("unknown");
    throw error;
  }
}

export async function sendAssistantMessage(
  { message, history, weekStart, locale }: AssistantRequest,
  { timeoutMs = ASSISTANT_TIMEOUT_MS, signal }: { timeoutMs?: number; signal?: AbortSignal } = {},
): Promise<AssistantTurn> {
  // Asked before anything leaves: a message we never sent can go straight back to the composer.
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    throw new AssistantError("offline_before");
  }

  const controller = new AbortController();
  let timedOut = false;
  const timeout = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  signal?.addEventListener("abort", () => controller.abort(), { once: true });

  try {
    const response = await fetch(ASSISTANT_ENDPOINTS.CHAT, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message, history, week_start: weekStart, locale }),
      signal: controller.signal,
    });

    if (!response.ok) {
      const body = await readErrorBody(response);
      throw new AssistantError(kindForStatus(response.status, body), {
        retryAfterSeconds: retryAfterFrom(response, body),
      });
    }

    const payload = await readTurn(response);
    if (!payload?.data?.reply) throw new AssistantError("server");
    return payload.data;
  } catch (error) {
    if (error instanceof AssistantError) throw error;
    const aborted = error instanceof Error && error.name === "AbortError";
    throw new AssistantError(kindForNetworkFailure({ timedOut, aborted }));
  } finally {
    clearTimeout(timeout);
  }
}

export default sendAssistantMessage;
